// TODO: try to fix as some point
/* eslint-disable @typescript-eslint/naming-convention */

/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { type Boundary, shift, size } from '@floating-ui/react';
import classNames from 'classnames';
import { format } from 'date-fns';
import { useCallback, useRef, useState } from 'react';
import ReactDatePicker, {
  type ReactDatePickerCustomHeaderProps,
} from 'react-datepicker';

import type { CommonProps, HtmlAttributes } from '../_common/types';
import { CleanIconButton } from '../clean-icon-button';
import { useIsInsideDialog } from '../dialog/dialog-context';
import { Divider } from '../divider';
import { ArrowLeftIconOutline } from '../icons/generated/heroIcons/ArrowLeftIconOutline';
import { ArrowRightIconOutline } from '../icons/generated/heroIcons/ArrowRightIconOutline';
import { CalendarDaysIconOutline } from '../icons/generated/heroIcons/CalendarDaysIconOutline';
import { ChevronDownIconOutline } from '../icons/generated/heroIcons/ChevronDownIconOutline';
import { TextInput } from '../text-input';
import { NeedleTime, TimePicker, type TimePickerProps } from '../time-picker';
import { TimeZonePicker } from '../timezone-picker';
import { Typography } from '../typography';
import { daysInMonth, getYearsPeriodString } from './utils';

type DatePickerTextInputWrapperProps = React.ComponentProps<'input'> & {
  textInputProps?: React.ComponentProps<typeof TextInput>;
  isDisabled?: boolean;
  isClearable?: boolean;
  value?: string;
  ariaLabel?: string;
};

// This wrapper is needed due to react-datepicker injecting html attributes
// into the custom input component on root level, since we use htmlAttributes
// we need to help spread them there instead of the root level
const DatePickerTextInputWrapper = ({
  textInputProps,
  isDisabled,
  isClearable,
  value,
  placeholder,
  ...restProps
}: DatePickerTextInputWrapperProps) => {
  // deconstruct textInputProps to avoid bugs with react-datepicker injecting props
  const {
    placeholder: textInputPlaceholder,
    htmlAttributes: textInputHtmlAttributes,
    onChange: textInputOnChange,
    ref,
    ...restTextInputProps
  } = textInputProps ?? {};
  const { onChange: injectedOnChange, ...restInjectedProps } = restProps;

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    injectedOnChange?.(event);
    textInputOnChange?.(event);
  };

  return (
    <TextInput
      ref={ref}
      trailingElement={<CalendarDaysIconOutline />}
      value={value}
      placeholder={placeholder ?? textInputPlaceholder}
      isDisabled={isDisabled}
      isClearable={isClearable}
      onChange={handleChange}
      // react-datepicker injects html attributes into the custom input
      htmlAttributes={{
        ...textInputHtmlAttributes,
        ...restInjectedProps,
      }}
      {...restTextInputProps}
    />
  );
};

/**
 *
 *
 * Types
 *
 *
 */

type PickerType = 'day' | 'month' | 'year';

// Define a more flexible type for onChange
type DatePickerChangeHandler = (
  date: Date | null | [Date | null, Date | null] | Date[],
  event?: React.SyntheticEvent | undefined,
) => void;

/**
 * @remarks
 * The `reactDatePickerProps` prop is forwarded to the underlying `react-datepicker` component. See the [react-datepicker documentation](https://reactdatepicker.com/) for more information.
 *
 * @remarks
 * Where `TextInputProps` is defined in the `TextInput` component and `TimePickerProps` is defined in the `TimePicker` component.
 */
interface DatePickerProps {
  /** Props for the embedded `TextInput` component. */
  // TODO v5: this forwards TextInput's props, so whatever happens to its
  // inconsistently named ones lands here too and needs the same codemod.
  textInputProps?: React.ComponentProps<typeof TextInput>;
  /** Whether the date picker is disabled. */
  isDisabled?: boolean;
  /**
   * Whether to show the clear button.
   * @default false
   */
  isClearable?: boolean;
  /** Props for the embedded `TimePicker` component. */
  timePickerProps?: TimePickerProps;
  /** Props for the embedded `TimeZonePicker` component. */
  timeZonePickerProps?: React.ComponentProps<typeof TimeZonePicker>;
  /** Props forwarded to underlying `react-datepicker` component. */
  reactDatePickerProps: React.ComponentProps<typeof ReactDatePicker>;
  /**
   * WARNING: these attributes do not apply to the underlying input element currently. This will be fixed in a future version.
   */
  htmlAttributes?: HtmlAttributes<'div'>;
}

export const DatePicker = ({
  reactDatePickerProps,
  textInputProps,
  timePickerProps,
  timeZonePickerProps,
  isDisabled,
  isClearable = false,
  ref,
  className,
  style,
  htmlAttributes,
  ...restProps
}: CommonProps<'div', DatePickerProps>) => {
  const classes = classNames(`ndl-datepicker`, className, {});
  const datetimeRef = useRef<ReactDatePicker>(null);
  const shouldRestoreFocusAfterEscapeRef = useRef(false);
  const [picker, setPicker] = useState<PickerType>('day');
  const [preSelectedDate, setPreSelectedDate] = useState<Date | null>(
    (reactDatePickerProps.selected as Date | null) ||
      (reactDatePickerProps.startDate as Date | null),
  );
  const [selectedTimeZone, setSelectedTimeZone] = useState<string | undefined>(
    timeZonePickerProps?.value,
  );

  const isInsideDialog = useIsInsideDialog();

  // Inside a Dialog the popper is fixed and not portaled. A transformed
  // ancestor (e.g. Popover) can make Floating UI under-measure available
  // space, so use the viewport as the boundary instead.
  const floatingBoundary: Boundary = isInsideDialog ? [] : 'clippingAncestors';

  const headerAction = useCallback(
    (action: PickerType) => {
      setPicker(picker === action ? 'day' : action);
    },
    [picker],
  );

  const CustomHeader = useCallback(
    (props: ReactDatePickerCustomHeaderProps) => {
      const {
        decreaseMonth,
        increaseMonth,
        increaseYear,
        decreaseYear,
        nextMonthButtonDisabled,
        nextYearButtonDisabled,
        prevMonthButtonDisabled,
        prevYearButtonDisabled,
      } = props;

      const prevCallback = picker === 'year' ? decreaseYear : decreaseMonth;
      const nextCallback = picker === 'year' ? increaseYear : increaseMonth;
      const prevDisabled =
        picker === 'year' ? prevYearButtonDisabled : prevMonthButtonDisabled;
      const nextDisabled =
        picker === 'year' ? nextYearButtonDisabled : nextMonthButtonDisabled;

      return (
        <div className="ndl-datepicker-header">
          <div className="ndl-datepicker-selects">
            {picker !== 'year' && (
              <button
                type="button"
                aria-label={`${picker === 'month' ? 'Close' : 'Open'} month picker`}
                aria-pressed={picker === 'month'}
                onClick={() => headerAction('month')}
              >
                <div className="ndl-datepicker-select-content">
                  <Typography variant="subheading-small">
                    {format(props.date, 'MMM')}
                  </Typography>
                  <ChevronDownIconOutline
                    className={classNames('ndl-datepicker-chevron', {
                      'ndl-open': picker === 'month',
                    })}
                    aria-label="Chevron icon"
                  />
                </div>
              </button>
            )}
            {picker !== 'month' && (
              <button
                type="button"
                aria-label={`${picker === 'year' ? 'Close' : 'Open'} year picker`}
                onClick={() => headerAction('year')}
              >
                <div className="ndl-datepicker-select-content">
                  <Typography variant="subheading-small">
                    {picker === 'year'
                      ? getYearsPeriodString(
                          props.date,
                          reactDatePickerProps?.yearItemNumber,
                        )
                      : format(props.date, 'yyyy')}
                  </Typography>
                  <ChevronDownIconOutline
                    className={classNames('ndl-datepicker-chevron', {
                      'ndl-open': picker === 'year',
                    })}
                    aria-label="Chevron icon"
                  />
                </div>
              </button>
            )}
          </div>
          {picker !== 'month' && (
            <div className="ndl-datepicker-nav">
              <CleanIconButton
                description={
                  picker === 'year' ? 'Previous period' : 'Previous month'
                }
                onClick={prevCallback}
                isDisabled={prevDisabled}
                size="small"
              >
                <ArrowLeftIconOutline />
              </CleanIconButton>
              <CleanIconButton
                description={picker === 'year' ? 'Next period' : 'Next month'}
                onClick={nextCallback}
                isDisabled={nextDisabled}
                size="small"
              >
                <ArrowRightIconOutline />
              </CleanIconButton>
            </div>
          )}
        </div>
      );
    },
    [picker, headerAction, reactDatePickerProps?.yearItemNumber],
  );

  const handleTimeChange = (newTime: NeedleTime) => {
    if (
      reactDatePickerProps.selectsRange ||
      reactDatePickerProps.selectsMultiple
    ) {
      // this is consistent with the behavior of react-datepicker
      return;
    }

    if (!preSelectedDate) {
      return;
    }

    const newDate = new Date(preSelectedDate);
    newDate.setHours(newTime.hour, newTime.minute, 0, 0);
    setPreSelectedDate(newDate);

    if (reactDatePickerProps.onChange) {
      // For single date selection
      reactDatePickerProps.onChange(newDate, undefined);
    }
  };

  const handleTimeZoneChange = (timezone: string) => {
    setSelectedTimeZone(timezone);
    if (timeZonePickerProps?.onChange) {
      timeZonePickerProps.onChange(timezone);
    }
  };

  /**
   * Intercept onChange so we can work with
   * Month and Year pickers
   */
  const interceptedChange: DatePickerChangeHandler = useCallback(
    (
      date: Date | null | [Date | null, Date | null] | Date[],
      event?: React.SyntheticEvent,
    ) => {
      if (picker !== 'day') {
        // setTimeout to prevent picker change before the handleMonthChange logic
        setTimeout(() => {
          setPicker('day');
        });
      } else if (picker === 'day') {
        if (date instanceof Date) {
          setPreSelectedDate(date);
        } else if (
          Array.isArray(date) &&
          date.length > 0 &&
          date[0] instanceof Date
        ) {
          setPreSelectedDate(date[0]);
        } else if (date === null) {
          setPreSelectedDate(null);
        }

        if (reactDatePickerProps.onChange && date !== null) {
          (reactDatePickerProps.onChange as DatePickerChangeHandler)(
            date,
            event,
          );
        }
      }
    },
    [picker, reactDatePickerProps],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // react-datepicker prevents Escape when it closes the calendar, but
    // otherwise lets the event bubble and dismiss ancestor overlays.
    if (event.key === 'Escape' && event.defaultPrevented) {
      shouldRestoreFocusAfterEscapeRef.current = true;
      event.stopPropagation();
    }
    htmlAttributes?.onKeyDown?.(event);
  };

  /** These intercepts are needed for webkit to ensure that focus is restored to the input when the calendar is closed. */
  const interceptedOnBlur = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      reactDatePickerProps.onBlur?.(event);

      if (
        !shouldRestoreFocusAfterEscapeRef.current ||
        event.relatedTarget !== null
      ) {
        return;
      }

      const input = event.currentTarget;
      queueMicrotask(() => {
        if (
          shouldRestoreFocusAfterEscapeRef.current &&
          input.isConnected &&
          document.activeElement === document.body
        ) {
          datetimeRef.current?.sendFocusBackToInput();
        }
        shouldRestoreFocusAfterEscapeRef.current = false;
      });
    },
    [reactDatePickerProps],
  );

  const interceptedOnFocus = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      reactDatePickerProps.onFocus?.(event);

      if (!shouldRestoreFocusAfterEscapeRef.current) {
        return;
      }

      // react-datepicker schedules both focus and blur while closing. If blur
      // ran first, focus is now stable and the Escape marker can be cleared.
      requestAnimationFrame(() => {
        shouldRestoreFocusAfterEscapeRef.current = false;
      });
    },
    [reactDatePickerProps],
  );

  /**
   * Intercept onCalendarClose so we can
   * switch to "day" picker if we close on "month" or "year"
   * view
   */
  const interceptedOnCalendarClose = useCallback(() => {
    if (picker !== 'day') {
      setPicker('day');
    }
    reactDatePickerProps?.onCalendarClose?.();
  }, [picker, reactDatePickerProps]);

  const handleMonthChange = useCallback(
    (date: Date) => {
      if (picker === 'month') {
        const selectedDay = preSelectedDate?.getDate() ?? 1;
        const daysInNewMonth = daysInMonth(date.getMonth(), date.getFullYear());
        date.setDate(Math.min(selectedDay, daysInNewMonth));
        reactDatePickerProps?.onMonthChange?.(date);
      } else if (picker === 'year') {
        const selectedDay = preSelectedDate?.getDate() ?? 1;
        const selectedMonth = preSelectedDate?.getMonth() ?? 0;
        const daysInNewMonth = daysInMonth(selectedMonth, date.getFullYear());
        date.setMonth(selectedMonth, Math.min(selectedDay, daysInNewMonth));
        reactDatePickerProps?.onMonthChange?.(date);
      }
      setPreSelectedDate(date);
    },
    [picker, preSelectedDate, reactDatePickerProps],
  );

  /**
   * Intercept onChangeRaw to handle clearing the input
   * When the user clears the text input, set the date to null
   */
  const interceptedOnChangeRaw = useCallback(
    (
      event?:
        | React.KeyboardEvent<HTMLElement>
        | React.MouseEvent<HTMLElement, MouseEvent>,
    ) => {
      const target = event?.target as HTMLInputElement | undefined;
      if (target?.value === '') {
        setPreSelectedDate(null);
        if (reactDatePickerProps.onChange) {
          (reactDatePickerProps.onChange as DatePickerChangeHandler)(
            null,
            event,
          );
        }
      }
      reactDatePickerProps?.onChangeRaw?.(event);
    },
    [reactDatePickerProps],
  );

  const datePickerProps = {
    ...reactDatePickerProps,
    onBlur: interceptedOnBlur,
    onCalendarClose: interceptedOnCalendarClose,
    onChange: interceptedChange,
    onChangeRaw: interceptedOnChangeRaw,
    onFocus: interceptedOnFocus,
    onMonthChange: handleMonthChange,
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- the key handlers only stop Escape from propagating, they add no interaction
    <div
      className={classes}
      ref={ref}
      style={style}
      {...restProps}
      {...htmlAttributes}
      onKeyDown={handleKeyDown}
    >
      {/** TODO v5: Remove the div wrapper, type the component as an input, and apply all root props to the TextInput */}
      <ReactDatePicker
        ref={datetimeRef}
        customInput={
          <DatePickerTextInputWrapper
            isDisabled={isDisabled}
            isClearable={isClearable}
            textInputProps={textInputProps}
          />
        }
        customTimeInput={
          <span className="ndl-time-picker-wrapper">
            <Divider />
            <TimePicker
              isFluid
              value={
                preSelectedDate
                  ? new NeedleTime(
                      preSelectedDate.getHours(),
                      preSelectedDate.getMinutes(),
                    )
                  : undefined
              }
              onChange={handleTimeChange}
              {...timePickerProps}
              floatingStrategy="absolute"
              isPortaled={false}
            />
            {timeZonePickerProps && (
              <TimeZonePicker
                isFluid
                value={selectedTimeZone}
                onChange={handleTimeZoneChange}
                referenceDate={preSelectedDate || undefined}
                {...timeZonePickerProps}
                floatingStrategy="absolute"
                isPortaled={false}
              />
            )}
          </span>
        }
        disabled={isDisabled}
        showPopperArrow={false}
        showMonthYearPicker={picker === 'month'}
        showYearPicker={picker === 'year'}
        shouldCloseOnSelect={
          picker === 'day' && !(reactDatePickerProps.showTimeInput ?? false)
        }
        dayClassName={() => 'ndl-datepicker-day'}
        renderCustomHeader={CustomHeader}
        {...datePickerProps}
        isClearable={isClearable}
        popperClassName={classNames(
          'ndl-datepicker-popper',
          reactDatePickerProps.popperClassName,
        )}
        popperModifiers={[
          shift({ boundary: floatingBoundary, padding: 8 }),
          size({
            apply({ availableHeight, availableWidth, elements }) {
              elements.floating.style.setProperty(
                '--ndl-datepicker-available-height',
                `${availableHeight}px`,
              );
              Object.assign(elements.floating.style, {
                maxWidth: `${availableWidth}px`,
              });
            },
            boundary: floatingBoundary,
            padding: 8,
          }),
          ...(reactDatePickerProps.popperModifiers ?? []),
        ]}
        popperProps={{
          strategy: isInsideDialog ? 'fixed' : 'absolute',
          ...reactDatePickerProps.popperProps,
        }}
      />
    </div>
  );
};
